import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, ErrorNotice, Loading } from '../components/Feedback';
import { SenoIcon } from '../components/SenoIcon';
import { get, patch, post } from '../lib/api';
import type { Chapter, ContentBlock, Section } from '../types';

type DocumentData = { chapters: Chapter[]; sections: Section[]; blocks: ContentBlock[] };
type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type Snapshot = { id: string; label: string; kind: string; createdAt: string };

function BlockEditor({
  projectId,
  block,
  onSaved
}: {
  projectId: string;
  block: ContentBlock;
  onSaved: (block: ContentBlock) => void;
}) {
  const localKey = `senoskripsi:draft:${block.id}`;
  const serverText = String(block.content.text ?? '');
  const [text, setText] = useState(() => localStorage.getItem(localKey) ?? serverText);
  const [revision, setRevision] = useState(block.revision);
  const [status, setStatus] = useState<SaveState>('idle');
  const [message, setMessage] = useState('');
  const first = useRef(true);
  const syncingFromServer = useRef(false);
  const latest = useRef({ block, onSaved, projectId, revision });
  latest.current = { block, onSaved, projectId, revision };

  useEffect(() => {
    if (block.revision === revision) return;

    const localDraft = localStorage.getItem(localKey);

    if (localDraft !== null && localDraft !== serverText) {
      setStatus('error');
      setMessage('Versi server berubah. Draft lokal tetap aman; tinjau sebelum menyimpan.');
      return;
    }

    if (text !== serverText) {
      syncingFromServer.current = true;
      setText(serverText);
    }

    setRevision(block.revision);
    setStatus('idle');
    setMessage('');
  }, [block.revision, localKey, revision, serverText, text]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }

    if (syncingFromServer.current) {
      syncingFromServer.current = false;
      return;
    }

    localStorage.setItem(localKey, text);
    setStatus('saving');
    const timer = window.setTimeout(async () => {
      const current = latest.current;
      try {
        const result = await patch<{ revision: number; savedAt: string }>(
          `/api/projects/${current.projectId}/blocks/${current.block.id}`,
          {
            content: { ...current.block.content, text },
            expectedRevision: current.revision,
            reason: 'autosave'
          }
        );
        setRevision(result.revision);
        setStatus('saved');
        setMessage('Tersimpan');
        localStorage.removeItem(localKey);
        current.onSaved({
          ...current.block,
          content: { ...current.block.content, text },
          revision: result.revision,
          updatedAt: result.savedAt
        });
      } catch (caught) {
        setStatus('error');
        setMessage(
          caught instanceof Error ? caught.message : 'Gagal menyimpan — perubahan lokal tetap aman'
        );
      }
    }, 850);
    return () => window.clearTimeout(timer);
  }, [localKey, text]);

  return (
    <article className="editor-block">
      <div className="block-meta">
        <span>Paragraf</span>
        <span className={`autosave autosave-${status}`}>
          {status === 'saving'
            ? 'Menyimpan…'
            : status === 'error'
              ? message
              : status === 'saved'
                ? 'Tersimpan'
                : `Versi ${revision}`}
        </span>
      </div>
      <textarea
        aria-label="Isi paragraf"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="Mulai tulis isi bagian ini. Perubahan disimpan otomatis…"
        rows={8}
      />
    </article>
  );
}

export function DocumentPage({ projectId }: { projectId: string }) {
  const [data, setData] = useState<DocumentData>({ chapters: [], sections: [], blocks: [] });
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newSection, setNewSection] = useState('');
  const [instruction, setInstruction] = useState('');
  const [aiWorking, setAiWorking] = useState(false);
  const [candidate, setCandidate] = useState<{
    id: string;
    status: string;
    candidate: { text: string };
    validation: { issues: Array<{ message: string; severity: string }> };
  } | null>(null);

  const load = useCallback(async () => {
    try {
      const [document, history] = await Promise.all([
        get<DocumentData>(`/api/projects/${projectId}/document`),
        get<{ snapshots: Snapshot[] }>(`/api/projects/${projectId}/snapshots`)
      ]);
      setData(document);
      setSnapshots(history.snapshots);
      setSelectedSectionId((current) => current || document.sections[0]?.id || '');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Dokumen tidak dapat dimuat.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = data.sections.find((section) => section.id === selectedSectionId);
  const selectedChapter = data.chapters.find((chapter) => chapter.id === selected?.chapterId);
  const selectedBlocks = useMemo(
    () => data.blocks.filter((block) => block.sectionId === selectedSectionId),
    [data.blocks, selectedSectionId]
  );

  async function addSection(chapterId: string) {
    if (!newSection.trim()) return;
    try {
      const result = await post<{ section: Section }>(`/api/projects/${projectId}/sections`, {
        chapterId,
        title: newSection,
        level: 1
      });
      setData((current) => ({ ...current, sections: [...current.sections, result.section] }));
      setSelectedSectionId(result.section.id);
      setNewSection('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Bagian gagal dibuat.');
    }
  }

  async function addBlock() {
    if (!selectedSectionId) return;
    try {
      const result = await post<{ block: ContentBlock }>(`/api/projects/${projectId}/blocks`, {
        sectionId: selectedSectionId,
        type: 'paragraph',
        content: { text: '' }
      });
      setData((current) => ({ ...current, blocks: [...current.blocks, result.block] }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Paragraf gagal ditambahkan.');
    }
  }

  async function renameSection() {
    if (!selected) return;
    const title = window.prompt('Judul bagian', selected.title)?.trim();
    if (!title || title === selected.title) return;
    try {
      const result = await patch<{ title: string; revision: number }>(
        `/api/projects/${projectId}/sections/${selected.id}`,
        { title, expectedRevision: selected.revision }
      );
      setData((current) => ({
        ...current,
        sections: current.sections.map((section) =>
          section.id === selected.id
            ? { ...section, title: result.title, revision: result.revision }
            : section
        )
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Judul bagian gagal diperbarui.');
    }
  }

  async function createSnapshot() {
    const defaultLabel = `Snapshot ${new Date().toLocaleString('id-ID')}`;
    const label = window.prompt('Beri nama snapshot agar mudah dikenali:', defaultLabel)?.trim();

    if (!label) return;

    try {
      await post(`/api/projects/${projectId}/snapshots`, {
        label,
        kind: 'manual'
      });
      await load();
      setHistoryOpen(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Snapshot gagal dibuat.');
    }
  }

  async function restore(snapshot: Snapshot) {
    const createdAt = new Date(snapshot.createdAt).toLocaleString('id-ID');

    if (
      !window.confirm(
        `Pulihkan snapshot "${snapshot.label}" dari ${createdAt}? Kondisi dokumen saat ini akan disimpan sebagai cadangan otomatis terlebih dahulu.`
      )
    )
      return;

    try {
      await post(`/api/projects/${projectId}/snapshots/${snapshot.id}/restore`);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Snapshot gagal dipulihkan.');
    }
  }

  async function createAiCandidate(taskType: 'section_generate' | 'paraphrase') {
    const target = selectedBlocks[0];
    if (!target) {
      setError('Tambahkan satu paragraf terlebih dahulu sebagai tujuan usulan.');
      return;
    }
    setAiWorking(true);
    setError('');
    try {
      const result = await post<{ candidate: NonNullable<typeof candidate> }>(
        `/api/projects/${projectId}/ai-candidates`,
        {
          taskType,
          targetType: 'block',
          targetId: target.id,
          instruction:
            instruction ||
            (taskType === 'paraphrase'
              ? 'Parafrase akademik tanpa mengubah fakta atau sitasi.'
              : `Susun bagian ${selected?.title ?? ''} berdasarkan konteks proyek dan sumber yang tersedia.`),
          relevantObjectIds: selectedBlocks.map((block) => block.id),
          idempotencyKey: `${taskType}-${target.id}-${target.revision}-${crypto.randomUUID()}`
        }
      );
      setCandidate(result.candidate);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Seno AI belum berhasil membuat usulan.');
    } finally {
      setAiWorking(false);
    }
  }

  async function applyCandidate() {
    const target = selectedBlocks[0];
    if (!candidate || !target) return;

    const draftKey = `senoskripsi:draft:${target.id}`;
    const localDraft = localStorage.getItem(draftKey);
    const currentServerText = String(target.content.text ?? '');

    if (localDraft !== null && localDraft !== currentServerText) {
      setError(
        'Masih ada perubahan lokal yang belum selesai disimpan. Tunggu sampai status Tersimpan lalu terapkan usulan lagi.'
      );
      return;
    }

    setAiWorking(true);
    setError('');

    try {
      const result = await post<{
        ok: boolean;
        revision: number;
        appliedAt: string;
      }>(`/api/projects/${projectId}/ai-candidates/${candidate.id}/apply`, {
        expectedRevision: target.revision
      });

      const appliedText = candidate.candidate.text;

      localStorage.removeItem(draftKey);

      setData((current) => ({
        ...current,
        blocks: current.blocks.map((block) =>
          block.id === target.id
            ? {
                ...block,
                content: {
                  ...block.content,
                  text: appliedText
                },
                revision: result.revision,
                updatedAt: result.appliedAt
              }
            : block
        )
      }));

      setCandidate(null);
      setInstruction('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Usulan belum dapat diterapkan.');
    } finally {
      setAiWorking(false);
    }
  }

  if (loading) return <Loading label="Menyusun struktur dokumen…" />;
  return (
    <div className="document-workspace">
      <aside className="document-tree" aria-label="Struktur dokumen">
        <div className="tree-title">
          <span className="eyebrow">Struktur</span>
          <strong>Chapter → Section</strong>
        </div>
        <nav>
          {data.chapters.map((chapter) => (
            <div className="chapter-group" key={chapter.id}>
              <div className="chapter-label">
                <span>BAB {chapter.position}</span>
                <strong>{chapter.title}</strong>
              </div>
              {data.sections
                .filter((section) => section.chapterId === chapter.id)
                .map((section) => (
                  <button
                    className={selectedSectionId === section.id ? 'active' : ''}
                    key={section.id}
                    onClick={() => setSelectedSectionId(section.id)}
                  >
                    <span>
                      {chapter.position}.{section.position}
                    </span>
                    {section.title}
                  </button>
                ))}
              <div className="inline-add">
                <input
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  placeholder="Bagian baru"
                  aria-label={`Bagian baru di ${chapter.title}`}
                />
                <button aria-label="Tambah bagian" onClick={() => addSection(chapter.id)}>
                  +
                </button>
              </div>
            </div>
          ))}
        </nav>
      </aside>
      <main className="section-editor">
        {error ? <ErrorNotice message={error} /> : null}
        {selected ? (
          <>
            <header className="editor-heading">
              <div>
                <span className="breadcrumb">
                  BAB {selectedChapter?.position} / {selectedChapter?.title}
                </span>
                <h1>{selected.title}</h1>
                <p>Mode Section Focus — perubahan tersimpan otomatis dengan perlindungan versi.</p>
              </div>
              <div className="editor-actions">
                <button className="button button-ghost" onClick={renameSection}>
                  Ubah judul
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => setHistoryOpen(!historyOpen)}
                >
                  <SenoIcon name="history" /> Riwayat
                </button>
                <button className="button button-primary" onClick={createSnapshot}>
                  <SenoIcon name="snapshot" /> Buat Snapshot
                </button>
              </div>
            </header>
            <section className="seno-compose">
              <div className="compose-heading">
                <SenoIcon name="ai" />
                <div>
                  <strong>Bantu susun bagian ini</strong>
                  <span>
                    Usulan diperiksa fakta, istilah, evidence, dan sumber sebelum dapat diterapkan.
                  </span>
                </div>
              </div>
              <textarea
                rows={3}
                value={instruction}
                onChange={(event) => setInstruction(event.target.value)}
                placeholder="Contoh: perkuat latar belakang dengan referensi yang sudah tersimpan"
              />
              <div className="row-actions">
                <button
                  className="button button-primary"
                  disabled={aiWorking}
                  onClick={() => createAiCandidate('section_generate')}
                >
                  {aiWorking ? 'Memeriksa…' : 'Susun usulan'}
                </button>
                <button
                  className="button button-secondary"
                  disabled={aiWorking}
                  onClick={() => createAiCandidate('paraphrase')}
                >
                  <SenoIcon name="parafrase" /> Parafrase aman
                </button>
              </div>
              {candidate ? (
                <div className={`candidate-preview ${candidate.status}`}>
                  <div>
                    <strong>
                      {candidate.status === 'valid' ? 'Usulan siap ditinjau' : 'Usulan ditahan'}
                    </strong>
                    <span>Belum masuk ke dokumen utama.</span>
                  </div>
                  <textarea rows={8} value={candidate.candidate.text} readOnly />
                  {candidate.validation.issues.length ? (
                    <ul>
                      {candidate.validation.issues.map((issue, index) => (
                        <li key={`${issue.message}-${index}`}>{issue.message}</li>
                      ))}
                    </ul>
                  ) : null}
                  <button
                    className="button button-primary"
                    disabled={candidate.status !== 'valid' || aiWorking}
                    onClick={applyCandidate}
                  >
                    Terapkan ke dokumen
                  </button>
                </div>
              ) : null}
            </section>
            <div className="paper-preview">
              {selectedBlocks.length === 0 ? (
                <EmptyState
                  title="Bagian masih kosong"
                  description="Tambahkan paragraf pertama untuk mulai menulis."
                />
              ) : (
                selectedBlocks.map((block) => (
                  <BlockEditor
                    key={block.id}
                    projectId={projectId}
                    block={block}
                    onSaved={(saved) =>
                      setData((current) => ({
                        ...current,
                        blocks: current.blocks.map((item) => (item.id === saved.id ? saved : item))
                      }))
                    }
                  />
                ))
              )}
              <button className="add-block" onClick={addBlock}>
                + Tambah paragraf
              </button>
            </div>
          </>
        ) : (
          <EmptyState
            title="Pilih bagian"
            description="Pilih satu bagian dari struktur dokumen untuk mulai menulis."
          />
        )}
      </main>
      {historyOpen ? (
        <aside className="history-panel">
          <div className="panel-heading">
            <div>
              <span className="eyebrow">Recovery</span>
              <h2>Riwayat snapshot</h2>
            </div>
            <button aria-label="Tutup riwayat" onClick={() => setHistoryOpen(false)}>
              ×
            </button>
          </div>
          {snapshots.filter((snapshot) => snapshot.kind !== 'pre_restore').length === 0 ? (
            <p>Belum ada snapshot bernama. Klik Buat Snapshot untuk membuat titik pemulihan.</p>
          ) : (
            <div className="snapshot-list">
              {snapshots
                .filter((snapshot) => snapshot.kind !== 'pre_restore')
                .map((snapshot) => (
                  <article key={snapshot.id}>
                    <span className={`badge badge-${snapshot.kind}`}>
                      {snapshot.kind === 'automatic' ? 'Otomatis' : 'Snapshot'}
                    </span>
                    <strong>{snapshot.label}</strong>
                    <small>{new Date(snapshot.createdAt).toLocaleString('id-ID')}</small>
                    <button className="button button-ghost" onClick={() => restore(snapshot)}>
                      Pulihkan
                    </button>
                  </article>
                ))}
            </div>
          )}

          {snapshots.some((snapshot) => snapshot.kind === 'pre_restore') ? (
            <details className="snapshot-recovery">
              <summary>
                Cadangan pemulihan (
                {snapshots.filter((snapshot) => snapshot.kind === 'pre_restore').length})
              </summary>
              <p>
                Cadangan ini dibuat otomatis sebelum proses pemulihan dan disembunyikan dari daftar
                snapshot utama.
              </p>
              {snapshots
                .filter((snapshot) => snapshot.kind === 'pre_restore')
                .map((snapshot) => (
                  <article key={snapshot.id}>
                    <span className="badge badge-pre_restore">Cadangan</span>
                    <strong>Cadangan otomatis sebelum pemulihan</strong>
                    <small>{new Date(snapshot.createdAt).toLocaleString('id-ID')}</small>
                    <button
                      className="button button-ghost"
                      onClick={() =>
                        restore({
                          ...snapshot,
                          label: 'Cadangan otomatis sebelum pemulihan'
                        })
                      }
                    >
                      Pulihkan
                    </button>
                  </article>
                ))}
            </details>
          ) : null}
        </aside>
      ) : null}
    </div>
  );
}
